import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, User, UserPlus, X } from 'lucide-react';
import { calculateOptimalPopupPosition, cn, formatUserDisplayName } from '../../../lib/utils';
import { logger } from '../../../utils/logger';
import { Loader } from '../../ui';
import { MobileFullScreenModal } from '../../ui/MobileFullScreenModal';
import { useCollaborators } from '../../../hooks/queries/useCollaborators';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { cacheUser, getCachedUser } from '../../../utils/userCache';
import { getFormattedAssigneeName } from '../../../utils/assigneeHelper';
import { useViewport } from '../../../contexts/ViewportContext';
import { useTopicUIStore } from '../../../store/topicUIStore';
import { useQueryClient } from '@tanstack/react-query';
import { topicKeys } from '../../../hooks/queries/topicKeys';

interface TaskAssigneePickerProps {
  assignedUserId: string | null | undefined;
  onAssigneeChange: (userId: string | null) => void;
  disabled?: boolean;
  projectId?: string; // Optional project ID for dashboard tasks
}

export const TaskAssigneePicker: React.FC<TaskAssigneePickerProps> = ({
  assignedUserId,
  onAssigneeChange,
  disabled = false,
  projectId: providedProjectId, // Optional explicit project ID for dashboard tasks
}) => {
  const queryClient = useQueryClient();
  const { activeTopicId } = useTopicUIStore();

  // Get the current project ID from the active topic's data
  const getTopic = activeTopicId ? queryClient.getQueryData(topicKeys.detail(activeTopicId)) : null;
  const storeProjectId = getTopic ? (getTopic as any).projectId : null;

  // Get project ID from props (for dashboard) or from the active topic (for regular topics)
  const currentProjectId = providedProjectId || storeProjectId;

  // Make sure we have a valid project ID
  useEffect(() => {
    logger.component('TaskAssigneePicker project ID source', {
      hasProvidedId: !!providedProjectId,
      hasStoreId: !!storeProjectId,
      isUsingProvidedId: providedProjectId === currentProjectId,
    });
  }, [providedProjectId, storeProjectId, currentProjectId]);
  const [isOpen, setIsOpen] = useState(false);
  // Use React Query for collaborators
  const {
    data: collaborators = [],
    isLoading,
    error: collaboratorsError,
  } = useCollaborators(currentProjectId);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(collaboratorsError || null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();

  // Close dropdown when clicking outside, but enable it only after a short delay
  // This prevents race conditions with the click event on dropdown items
  const [outsideClickEnabled, setOutsideClickEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Delay enabling outside click to avoid interference with dropdown click handlers
      const timer = setTimeout(() => {
        setOutsideClickEnabled(true);
      }, 100);
      return () => {
        clearTimeout(timer);
        setOutsideClickEnabled(false);
      };
    }
  }, [isOpen]);

  useOutsideClick(
    dropdownRef,
    event => {
      // Don't handle outside clicks on mobile - the MobileFullScreenModal has its own close button
      if (isMobile) {
        return;
      }

      // For desktop: Check if the click event is on an element inside the dropdown
      const dropdownContent = document.querySelector('.assignee-dropdown-content');
      if (
        dropdownContent &&
        event?.target instanceof Node &&
        dropdownContent.contains(event.target)
      ) {
        // Don't close if clicking inside the dropdown content
        return;
      }

      if (isOpen) setIsOpen(false);
    },
    outsideClickEnabled
  );

  // Preload an image using crossOrigin anonymous to ensure it's correctly cached
  const preloadImage = (url: string) => {
    if (!url) return;

    try {
      // Add a user ID parameter that stays consistent for the same user
      // But doesn't change every time, to ensure proper caching
      const cacheUrl = url.includes('?') ? url : `${url}?cache=true`;

      const img = new Image();
      // Set crossOrigin to anonymous to ensure the image is properly cached
      img.crossOrigin = 'anonymous';

      // Add event listeners before setting src
      img.onload = () => {
        logger.component('Avatar image preloaded', { url: cacheUrl });
      };

      img.onerror = e => {
        logger.warn('Failed to preload avatar image', {
          url: cacheUrl,
          error: e instanceof Error ? e.message : String(e),
        });
      };

      // Set the source last to ensure listeners are ready
      img.src = cacheUrl;
    } catch (err) {
      logger.warn('Error in preloadImage', {
        url,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  // Helper function to get user's initials
  const getUserInitials = (
    user: { display_name?: string; email?: string } | null | undefined
  ): string => {
    if (!user) return '?';

    // Apply proper formatting to display name if available
    const displayName = user.display_name
      ? formatUserDisplayName(user.display_name)
      : user.email
        ? user.email.split('@')[0]
        : '';

    if (!displayName) return '?';

    // Get first letter of first and last name
    const initials = displayName
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('');

    return initials || displayName.charAt(0).toUpperCase();
  };

  // Cache and preload user avatars when collaborators data is available
  useEffect(() => {
    if (collaborators.length > 0) {
      logger.component('Caching collaborator users', {
        count: collaborators.length,
        collaboratorIds: collaborators.map(c => c.user_id),
        hasAssignedUser: assignedUserId
          ? collaborators.some(c => c.user_id === assignedUserId)
          : false,
      });

      // Store each user in our cache and forcefully preload their avatar
      collaborators.forEach(collab => {
        if (collab.user_id && collab.users_with_details) {
          // Add to our cache system
          cacheUser(collab.user_id, {
            id: collab.user_id,
            email: collab.users_with_details.email || '',
            display_name: collab.users_with_details.display_name || '',
            avatar_url: collab.users_with_details.avatar_url || undefined,
          });

          // Force-preload the image to prevent it from being refetched
          if (collab.users_with_details.avatar_url) {
            preloadImage(collab.users_with_details.avatar_url);
          }

          // Extra logging for the assigned user
          if (collab.user_id === assignedUserId) {
            logger.component('Assigned user found and cached', {
              userId: collab.user_id,
              displayName: collab.users_with_details.display_name,
              email: collab.users_with_details.email,
            });
          }
        }
      });
    }
  }, [collaborators, assignedUserId]);

  // Get the assigned user's details
  const assignedUser = collaborators.find(c => c.user_id === assignedUserId);

  // Log debugging information to track component state
  useEffect(() => {
    logger.component('TaskAssigneePicker state', {
      assignedUserId,
      assignedUserFound: !!assignedUser,
      collaboratorsCount: collaborators.length,
      collaboratorIds: collaborators.map(c => c.user_id),
      isCachedUserPresent: assignedUserId ? !!getCachedUser(assignedUserId) : false,
    });
  }, [assignedUserId, assignedUser, collaborators]);

  // Preload the assigned user's avatar when component mounts if there is one
  useEffect(() => {
    if (assignedUserId && assignedUser?.users_with_details?.avatar_url) {
      preloadImage(assignedUser.users_with_details.avatar_url);

      // Also cache the user's details
      if (assignedUser.user_id && assignedUser.users_with_details) {
        cacheUser(assignedUser.user_id, {
          id: assignedUser.user_id,
          email: assignedUser.users_with_details.email || '',
          display_name: assignedUser.users_with_details.display_name || '',
          avatar_url: assignedUser.users_with_details.avatar_url,
        });

        logger.component('TaskAssigneePicker: cached user from assignedUser', {
          userId: assignedUser.user_id,
          email: assignedUser.users_with_details.email,
          displayName: assignedUser.users_with_details.display_name,
        });
      }
    }
  }, [assignedUserId, assignedUser]);

  // Toggle the dropdown
  const handleToggleDropdown = () => {
    if (disabled) return;

    // If we're opening the dropdown, ensure all avatars are preloaded
    if (!isOpen) {
      // Force preload all avatars before showing the dropdown
      collaborators.forEach(collab => {
        if (collab.users_with_details?.avatar_url) {
          // Preload each avatar with a delay to prevent too many simultaneous requests
          setTimeout(() => {
            preloadImage(collab.users_with_details?.avatar_url || '');
          }, 10); // Small delay to stagger requests
        }
      });
    }

    setIsOpen(!isOpen);
  };

  // Handle selecting a collaborator
  const handleSelectCollaborator = async (userId: string | null) => {
    try {
      setIsAssigning(true);
      logger.component('TaskAssigneePicker: User selected', {
        userId: userId || 'null (unassign)',
        currentProjectId,
        isMobile,
      });

      // Call the callback which will update the parent component's state
      // This will update the form's display with the newly selected user
      onAssigneeChange(userId);

      // Always close the modal after selection (both desktop and mobile)
      setIsOpen(false);
    } catch (error) {
      logger.error('Error assigning task', {
        error: error instanceof Error ? error.message : String(error),
        userId,
        projectId: currentProjectId,
      });
      setError('Failed to assign task. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  // Log when we have an assignedUserId but no cached data
  useEffect(() => {
    if (assignedUserId && !getCachedUser(assignedUserId) && collaborators.length === 0) {
      logger.component('TaskAssigneePicker: Missing user data for assignee', {
        assignedUserId,
        isCached: !!getCachedUser(assignedUserId),
        collaboratorsCount: collaborators.length,
      });
    }
  }, [assignedUserId, collaborators.length]);

  // Get cached user data directly (as fallback)
  const cachedUserData = assignedUserId ? getCachedUser(assignedUserId) : null;

  // Log info about what data sources we have
  useEffect(() => {
    logger.component('TaskAssigneePicker: Data sources check', {
      hasAssignedUserId: !!assignedUserId,
      hasAssignedUserInCollaborators: !!assignedUser,
      hasCachedUserData: !!cachedUserData,
      assignedUserDisplayName: assignedUser?.users_with_details?.display_name || 'none',
      cachedUserDisplayName: cachedUserData?.display_name || 'none',
    });
  }, [assignedUserId, assignedUser, cachedUserData]);

  // Calculate dropdown position for desktop only
  const getDropdownPosition = () => {
    // Only handle desktop positioning now, mobile uses MobileFullScreenModal
    if (isMobile) return {};

    // For desktop, calculate optimal position based on button
    const buttonRect = dropdownRef.current?.querySelector('button')?.getBoundingClientRect();

    if (!buttonRect) return {};

    // Calculate a more reasonable height - either based on number of collaborators or max 200px
    const itemHeight = 48; // Approximate height of each collaborator item in pixels
    const headerFooterHeight = 20; // Extra space for padding and borders
    // Calculate height based on number of items, but max 200px
    const estimatedHeight = Math.min(collaborators.length * itemHeight + headerFooterHeight, 200);

    const position = calculateOptimalPopupPosition(
      { top: buttonRect.bottom, left: buttonRect.left },
      { width: 280, height: estimatedHeight },
      { x: 0, y: 8 }
    );

    return {
      position: 'fixed',
      top: `${position.top}px`,
      left: `${position.left}px`,
      zIndex: 100,
    };
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleToggleDropdown}
        className={cn(
          'flex items-center gap-0 text-blue-700 p-0',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        )}
        disabled={disabled}
      >
        {isAssigning ? (
          <>
            <Loader size="small" className="flex-shrink-0 mr-1.5" />
            <span>Updating...</span>
          </>
        ) : assignedUserId ? (
          <>
            {/* Always use the regular user icon instead of avatar or initials */}
            <User size={14} className="flex-shrink-0 mr-1.5 text-blue-700" />
            <span className="truncate max-w-[200px]">
              {getFormattedAssigneeName(assignedUserId, {
                fallbackName:
                  assignedUser?.users_with_details?.display_name || cachedUserData?.display_name,
                compact: false, // Use full name instead of compact format
              })}
            </span>
            <X
              size={14}
              className="flex-shrink-0 text-gray-500 hover:text-gray-700 ml-1.5"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                logger.component('TaskAssigneePicker: Unassign clicked');
                if (!disabled) handleSelectCollaborator(null);
              }}
            />
          </>
        ) : (
          <>
            <UserPlus size={14} className="flex-shrink-0 mr-1.5" />
            <span>Assign</span>
          </>
        )}
      </button>

      {/* Desktop dropdown */}
      {isOpen &&
        !isMobile &&
        ReactDOM.createPortal(
          <div
            style={getDropdownPosition()}
            className="w-auto bg-white rounded-md shadow-lg border border-gray-200 z-[999] overflow-visible"
            onClick={e => e.stopPropagation()}
          >
            {isLoading ? (
              <div className="p-2 text-sm text-gray-500">Loading collaborators...</div>
            ) : error ? (
              <div className="p-2 text-sm text-red-500">{error}</div>
            ) : (
              <div className="assignee-dropdown-content">
                {/* Error message */}
                {error && <div className="px-3 py-2 text-sm text-red-500">{error}</div>}
                {/* List of collaborators */}
                {collaborators.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500">No collaborators found</div>
                ) : (
                  collaborators.map(collaborator => (
                    <button
                      key={collaborator.user_id}
                      data-user-id={collaborator.user_id}
                      onClick={e => {
                        e.stopPropagation();
                        handleSelectCollaborator(collaborator.user_id);
                      }}
                      disabled={isAssigning}
                      className={cn(
                        'w-full text-left px-3 py-3.5 text-sm flex items-center',
                        collaborator.user_id === assignedUserId
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50',
                        isAssigning && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {collaborator.users_with_details?.avatar_url ? (
                        <img
                          src={collaborator.users_with_details.avatar_url}
                          alt="User avatar"
                          className="rounded-full flex-shrink-0 mr-2 object-cover w-8 h-8"
                          loading="eager"
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <div className="rounded-full flex-shrink-0 mr-2 bg-gray-300 flex items-center justify-center text-sm font-medium text-gray-600 w-8 h-8">
                          {getUserInitials(collaborator.users_with_details)}
                        </div>
                      )}
                      <span className="truncate">
                        {collaborator.user_id
                          ? getFormattedAssigneeName(collaborator.user_id, {
                              fallbackName: collaborator.users_with_details?.display_name,
                              compact: false, // Show full name in dropdown
                            })
                          : 'Unknown user'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>,
          document.body
        )}

      {/* Mobile full screen modal */}
      {isOpen && isMobile && (
        <MobileFullScreenModal
          isOpen={true}
          onClose={() => setIsOpen(false)}
          title="Assign Task"
          footerContent={
            <button
              onClick={() => setIsOpen(false)}
              className="w-full py-2.5 px-4 bg-blue-500 text-white font-medium rounded-md"
            >
              Cancel
            </button>
          }
        >
          {isLoading ? (
            <div className="p-4 flex items-center justify-center">
              <Loader size="small" />
              <span className="ml-2 text-gray-600">Loading collaborators...</span>
            </div>
          ) : error ? (
            <div className="p-4 text-red-500">{error}</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {/* Unassign option */}
              {assignedUserId && (
                <button
                  onClick={() => {
                    handleSelectCollaborator(null);
                  }}
                  className="w-full text-left px-4 py-4 flex items-center text-red-500"
                >
                  <div className="rounded-full flex-shrink-0 mr-3 bg-red-100 flex items-center justify-center w-10 h-10">
                    <X size={20} className="text-red-500" />
                  </div>
                  <span className="text-base">Unassign</span>
                </button>
              )}

              {/* Collaborator list */}
              {collaborators.length === 0 ? (
                <div className="p-4 text-gray-500">No collaborators found</div>
              ) : (
                collaborators.map(collaborator => (
                  <button
                    key={collaborator.user_id}
                    onClick={() => {
                      handleSelectCollaborator(collaborator.user_id);
                    }}
                    disabled={isAssigning}
                    className={cn(
                      'w-full text-left px-4 py-4 flex items-center',
                      collaborator.user_id === assignedUserId
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-700',
                      isAssigning && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {collaborator.users_with_details?.avatar_url ? (
                      <img
                        src={collaborator.users_with_details.avatar_url}
                        alt="User avatar"
                        className="rounded-full flex-shrink-0 mr-3 object-cover w-10 h-10"
                        loading="eager"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="rounded-full flex-shrink-0 mr-3 bg-gray-300 flex items-center justify-center text-sm font-medium text-gray-600 w-10 h-10">
                        {getUserInitials(collaborator.users_with_details)}
                      </div>
                    )}
                    <span className="text-base">
                      {collaborator.user_id
                        ? getFormattedAssigneeName(collaborator.user_id, {
                            fallbackName: collaborator.users_with_details?.display_name,
                            compact: false,
                          })
                        : 'Unknown user'}
                    </span>
                    {collaborator.user_id === assignedUserId && (
                      <Check size={18} className="ml-auto text-blue-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </MobileFullScreenModal>
      )}
    </div>
  );
};
